import type { AgentTask, AgentTaskType } from '@septima/contract';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiFetch } from '@/lib/api';
import { submitReviewDecisions } from '@/lib/agent-reviewables';
import { useSessionQuery } from '@/lib/auth';
import { useToast } from '@/lib/useToast';
import { clockNow } from '@/lib/clock';
import { inFlightTaskKey, nextAgentPollMs } from '@/lib/agent-poll';

let _generatedTaskId = 1000;
const nextTaskId = (): string => `agent-${++_generatedTaskId}`;

// Stable empty fallback for the tasks query. Using a fresh `[]` literal here
// makes the value referentially unstable across renders, which would re-fire
// the sync effect below on every render and spin a setTimeout → setState →
// render loop (~3k iterations/sec) whenever the query has no data.
const EMPTY_TASKS: AgentTask[] = [];

// The composed tasks query key. Module-scoped so the single literal is shared
// by the query, the internal invalidation, and the `refreshTasks` callback
// dispatch sites call after queueing a task out-of-band.
const TASKS_QUERY_KEY = ['agent', 'tasks'] as const;

// The caller's owner-scoped worklist (ADR-0010) — a separate server read from
// the firm-wide `TASKS_QUERY_KEY` feed. The Agent Kanban renders `worklistTasks`
// (act); the firm-wide `tasks` stays the source for the Agent Activity Log,
// badges, client-detail panels, and notifications (observe).
const WORKLIST_QUERY_KEY = ['agent', 'tasks', 'mine'] as const;

async function fetchTasks(): Promise<AgentTask[]> {
    const res = await api.agent.listTasks({ query: {} });
    if (res.status !== 200) throw new Error(`fetch ${res.status}`);
    return res.body.tasks as unknown as AgentTask[];
}

// The time limit for the confirmation of a task that `startTask` queued.
const START_CONFIRM_DEADLINE_MS = 60_000;

export interface AgentContextValue {
    tasks: AgentTask[];
    /** The caller's owner-scoped Agent Kanban worklist (ADR-0010). */
    worklistTasks: AgentTask[];
    /**
     * Has the task fetch resolved at least once (success or error)?
     *
     * `tasks` is `[]` both while the fetch is in flight and when there is
     * genuinely no agent activity. A surface that renders an "everything is
     * clear" empty state must not read the first as the second — the fetch is
     * slow enough to be visible (SEP-202). The counterpart to `isSettled` on
     * `useReviewQueue`.
     */
    tasksSettled: boolean;
    activeTasks: AgentTask[];
    reviewQueue: AgentTask[];
    startTask: (type: AgentTaskType, opts?: { clientId?: string; clientName?: string }) => string;
    // Resolves true when the decision committed server-side (so a host panel can
    // close on success). Applies an optimistic local update immediately.
    reviewTask: (taskId: string, action: 'approve' | 'reject', reason?: string) => Promise<boolean>;
    dismissTask: (taskId: string) => void;
    getTasksForClient: (clientId: string) => AgentTask[];
    // Refetch the composed tasks list. Dispatch sites that queue an AgentTask
    // through a route other than this context (insertTask / triggerComposer /
    // runDetector) must call this after a successful queue, so the new
    // queued/running task lands in `tasks` and starts the in-flight poll —
    // otherwise the sidebar badge stays "idle" until the next natural refetch.
    // The call also starts the poll cadence again at its fastest step.
    refreshTasks: () => void;
}

const AgentContext = createContext<AgentContextValue | null>(null);

export function AgentProvider({ children }: { children: ReactNode }) {
    // Local optimistic state: merge of dbTasks + any in-flight optimistic
    // inserts that haven't landed in the query yet.
    const [localTasks, setLocalTasks] = useState<AgentTask[]>([]);

    // Poll the composed tasks endpoint while a task is in flight, so the
    // queued → running → completed transitions of the runner show in the UI
    // with no manual reload. AgentRuntime advances the AgentJob row after
    // `apiFetch('/api/agent/run')` returns, so no local mutation invalidates
    // the query when the agent finishes. The poll stops when no task is in
    // flight. The delay starts at 3 s and grows to 15 s (`nextAgentPollMs`),
    // because one read takes seconds and a job runs for a minute or more.
    // The epoch of the cadence moves to "now" when the set of in-flight tasks
    // changes, or when `startTask` or `refreshTasks` runs.
    const inFlightKey = inFlightTaskKey(localTasks);
    const hasInFlightTask = inFlightKey !== '';
    // `key: null` makes the next interval calculation set a new epoch.
    const pollEpochRef = useRef<{ key: string | null; at: number }>({ key: null, at: 0 });
    const queryClient = useQueryClient();
    // The agent-tasks endpoint requires an authenticated staff session. This
    // provider is always mounted (including on the unauthenticated /login
    // screen), so gate the poll on the shared session query: an unauthenticated
    // visitor never fires the request, and the failing-tasks toast below can't
    // appear on the sign-in page. `useSessionQuery` reads the same cache key as
    // SessionProvider, so this adds no extra request.
    const { data: session } = useSessionQuery();
    const isAuthenticated = session?.authenticated ?? false;
    const {
        data: dbTasksRaw,
        isLoading,
        isError,
        // `isFetched` covers the error path too — a query that failed every
        // retry has settled, and a consumer must stop showing a spinner.
        isFetched: tasksSettled,
    } = useQuery<AgentTask[]>({
        queryKey: TASKS_QUERY_KEY,
        queryFn: fetchTasks,
        enabled: isAuthenticated,
        // TanStack Query calls this function after each fetch, and the timer
        // starts from that call. The value depends on the time of the last
        // fetch only, so a re-render between two fetches does not move the
        // timer.
        refetchInterval: hasInFlightTask
            ? (query) => {
                  if (pollEpochRef.current.key !== inFlightKey) {
                      pollEpochRef.current = { key: inFlightKey, at: clockNow().getTime() };
                  }
                  const lastFetchAt = Math.max(
                      query.state.dataUpdatedAt,
                      query.state.errorUpdatedAt,
                  );
                  return nextAgentPollMs(lastFetchAt - pollEpochRef.current.at);
              }
            : false,
        retry: 2,
    });
    const dbTasks = dbTasksRaw ?? EMPTY_TASKS;

    // The owner-scoped worklist read (ADR-0010). Self-scoped server-side from
    // the session — the request carries no scope/owner input. The worklist has
    // no timer of its own. Each worklist row is also a row of the firm-wide
    // feed, so the effect below refetches the worklist only when a poll of the
    // feed returns changed data. Structural sharing keeps the `dbTasksRaw`
    // reference when the data did not change.
    const { data: worklistRaw } = useQuery<AgentTask[]>({
        queryKey: WORKLIST_QUERY_KEY,
        queryFn: async () => {
            const res = await api.agent.listWorklist({ query: {} });
            if (res.status !== 200) throw new Error(`fetch ${res.status}`);
            return res.body.tasks as unknown as AgentTask[];
        },
        enabled: isAuthenticated,
        retry: 2,
    });
    const worklistDb = worklistRaw ?? EMPTY_TASKS;

    const previousTasksRef = useRef(dbTasksRaw);
    useEffect(() => {
        const previous = previousTasksRef.current;
        previousTasksRef.current = dbTasksRaw;
        // The first load of the feed is not a change: the worklist does its
        // own first load.
        if (previous === undefined || previous === dbTasksRaw) return;
        // `cancelRefetch: false` joins a worklist fetch that runs already
        // (for example from `refreshTasks`), and does not start a second one.
        void queryClient.invalidateQueries(
            { queryKey: WORKLIST_QUERY_KEY, exact: true },
            { cancelRefetch: false },
        );
    }, [dbTasksRaw, queryClient]);

    const refreshTasks = useCallback(() => {
        pollEpochRef.current = { key: null, at: 0 };
        // The tasks key is a prefix of the worklist key, so this call also
        // invalidates the worklist. The second call keeps the intent clear.
        void queryClient.invalidateQueries({ queryKey: TASKS_QUERY_KEY });
        void queryClient.invalidateQueries({ queryKey: WORKLIST_QUERY_KEY });
    }, [queryClient]);

    const { pushToast } = useToast();

    // Surface a failing tasks query instead of degrading silently. Without this
    // the query can settle in an error state (isLoading=false, data=undefined)
    // with no UI signal at all — the same condition that previously spun the
    // sync effect. Toast once per error episode (latched via ref) so the
    // in-flight refetch loop can't spam it, and clear the latch on recovery.
    const notifiedTasksErrorRef = useRef(false);
    useEffect(() => {
        // Never toast for an unauthenticated visitor — the poll is disabled for
        // them, so a stale error from a prior session must not surface on /login.
        if (isAuthenticated && isError) {
            if (!notifiedTasksErrorRef.current) {
                notifiedTasksErrorRef.current = true;
                pushToast({
                    kind: 'error',
                    message: 'Could not load agent tasks — the list may be out of date.',
                });
            }
        } else if (!isLoading) {
            notifiedTasksErrorRef.current = false;
        }
    }, [isAuthenticated, isError, isLoading, pushToast]);

    // Sync DB rows into localTasks. Take the DB row as authoritative for any
    // task that has landed there; keep purely-local optimistic tasks (those
    // not yet visible in the DB) so the UI doesn't flicker between insert
    // and the first refetch.
    // setState must live inside a setTimeout callback, not the effect body,
    // because React Compiler blocks the direct pattern.
    useEffect(() => {
        if (isLoading) return;
        const t = window.setTimeout(() => {
            setLocalTasks((prev) => {
                const next =
                    prev.length === 0
                        ? dbTasks
                        : dbTasks.concat(prev.filter((t) => !dbTasks.some((d) => d.id === t.id)));
                // Bail out with the existing reference when the merge is
                // element-wise identical, so React skips the re-render (and the
                // effect doesn't re-fire). Belt-and-braces against the spin loop.
                if (next.length === prev.length && next.every((task, i) => task === prev[i])) {
                    return prev;
                }
                return next;
            });
        }, 0);
        return () => window.clearTimeout(t);
    }, [dbTasks, isLoading]);

    const startTask = useCallback<AgentContextValue['startTask']>(
        (type, opts) => {
            const id = nextTaskId();
            const startedAt = clockNow().getTime();
            pollEpochRef.current = { key: null, at: 0 };

            // Optimistic local task (shows immediately in UI). All 17 production
            // agents are backend-owned per ADR-0010 — progress reads from the
            // AgentSession's Turns surfaced via task.agentSessionId, not from
            // client-seeded steps.
            const newTask: AgentTask = {
                id,
                type,
                status: 'queued',
                trigger: 'manual',
                triggerDetail: `Manual · ${opts?.clientName ?? 'firm-wide'}`,
                clientId: opts?.clientId,
                clientName: opts?.clientName,
                createdAt: clockNow().toISOString(),
                frontend: 'global',
                requiresReview: true,
                steps: [],
            };

            void (async () => {
                try {
                    const res = await apiFetch('/api/agent/run', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            type,
                            clientId: opts?.clientId,
                            clientName: opts?.clientName,
                            trigger: 'manual',
                        }),
                    });
                    const json = (await res.json()) as {
                        ok: boolean;
                        taskId?: string;
                    };

                    if (!json.ok || !json.taskId) {
                        setLocalTasks((prev) => prev.filter((t) => t.id !== id));
                        pushToast({
                            kind: 'error',
                            message: 'Failed to start agent task',
                        });
                        return;
                    }

                    const realTaskId = json.taskId;

                    // Poll until the real task is written to DB by the runtime's
                    // after() callback, then replace the optimistic placeholder.
                    // `fetchQuery` shares the cache entry of the feed: it joins
                    // a feed fetch that runs already, and its result also
                    // updates the feed. The delays use the backoff of the feed.
                    const poll = async (): Promise<void> => {
                        const elapsed = clockNow().getTime() - startedAt;
                        if (elapsed > START_CONFIRM_DEADLINE_MS) {
                            setLocalTasks((prev) => prev.filter((t) => t.id !== id));
                            pushToast({
                                kind: 'error',
                                message: 'Agent task did not confirm — refresh to check its status',
                            });
                            return;
                        }
                        try {
                            const rows = await queryClient.fetchQuery({
                                queryKey: TASKS_QUERY_KEY,
                                queryFn: fetchTasks,
                                staleTime: 0,
                            });
                            const realTask = rows.find((t) => t.id === realTaskId);
                            if (realTask) {
                                setLocalTasks((prev) => {
                                    const without = prev.filter((t) => t.id !== id);
                                    if (without.some((t) => t.id === realTaskId)) return without;
                                    return [...without, realTask];
                                });
                                return;
                            }
                        } catch {
                            // ignore fetch errors, retry
                        }
                        await new Promise((r) => setTimeout(r, nextAgentPollMs(elapsed)));
                        return poll();
                    };

                    // Brief initial delay gives after() time to start before the first poll.
                    setTimeout(() => void poll(), 1000);
                } catch {
                    setLocalTasks((prev) => prev.filter((t) => t.id !== id));
                    pushToast({
                        kind: 'error',
                        message: 'Failed to start agent task',
                    });
                }
            })();

            setLocalTasks((prev) => [...prev, newTask]);
            return id;
        },
        [pushToast, queryClient],
    );

    const reviewTask = useCallback<AgentContextValue['reviewTask']>(
        async (taskId, action, reason) => {
            // Optimistic local update — drops the task out of the review queue
            // immediately; the authoritative state lands on the next refetch.
            const patch: Partial<AgentTask> = {
                status: action === 'approve' ? 'approved' : 'rejected',
                reviewedAt: clockNow().toISOString(),
                rejectionReason: action === 'reject' ? reason : undefined,
            };
            setLocalTasks((prev) =>
                prev.map((task) => (task.id === taskId ? { ...task, ...patch } : task)),
            );

            // Route through the single review write surface (acts on the job's
            // primary artefact's Reviewable status, or audits at job level).
            const batch = await submitReviewDecisions([
                { target: 'task', taskId, decision: action, reason },
            ]);
            const ok = batch.ok && batch.summary.failed === 0;
            if (ok) {
                pushToast({
                    kind: 'success',
                    message: action === 'approve' ? 'Task approved' : 'Task rejected',
                });
            } else {
                pushToast({
                    kind: 'error',
                    message:
                        batch.error ?? batch.results[0]?.error ?? 'Failed to save review decision',
                });
            }
            refreshTasks();
            return ok;
        },
        [pushToast, refreshTasks],
    );

    const dismissTask = useCallback<AgentContextValue['dismissTask']>((taskId) => {
        setLocalTasks((prev) =>
            prev.filter((task) => {
                if (task.id !== taskId) return true;
                // Only dismiss completed/approved/rejected tasks.
                return (
                    task.status !== 'approved' &&
                    task.status !== 'completed' &&
                    task.status !== 'rejected'
                );
            }),
        );
    }, []);

    const getTasksForClient = useCallback<AgentContextValue['getTasksForClient']>(
        (clientId) => localTasks.filter((t) => t.clientId === clientId),
        [localTasks],
    );

    // The Kanban worklist: the server-scoped set, plus this user's optimistic
    // tasks that haven't landed in any DB read yet. A just-started task is the
    // caller's own (initiator == me), so it belongs on their worklist; merging
    // it here keeps the "Run" button responsive without waiting for a refetch.
    const worklistTasks = useMemo(() => {
        const localById = new Map(localTasks.map((t) => [t.id, t]));
        // Prefer the local copy for a row also present server-side — carries
        // reviewTask's optimistic patch (e.g. approved/rejected), so the
        // Needs-Review queue drops the item immediately instead of waiting on
        // the refetch that follows submitReviewDecisions.
        const merged = worklistDb.map((w) => localById.get(w.id) ?? w);
        const optimistic = localTasks.filter((t) => !dbTasks.some((d) => d.id === t.id));
        if (optimistic.length === 0) return merged;
        return merged.concat(optimistic.filter((o) => !worklistDb.some((w) => w.id === o.id)));
    }, [worklistDb, localTasks, dbTasks]);

    const activeTasks = useMemo(
        () => localTasks.filter((t) => t.status === 'running'),
        [localTasks],
    );
    const reviewQueue = useMemo(
        () => localTasks.filter((t) => t.status === 'review_required'),
        [localTasks],
    );

    const value = useMemo<AgentContextValue>(
        () => ({
            tasks: localTasks,
            worklistTasks,
            tasksSettled,
            activeTasks,
            reviewQueue,
            startTask,
            reviewTask,
            dismissTask,
            getTasksForClient,
            refreshTasks,
        }),
        [
            localTasks,
            worklistTasks,
            tasksSettled,
            activeTasks,
            reviewQueue,
            startTask,
            reviewTask,
            dismissTask,
            getTasksForClient,
            refreshTasks,
        ],
    );

    return <AgentContext.Provider value={value}>{children}</AgentContext.Provider>;
}

export function useAgent(): AgentContextValue {
    const value = useContext(AgentContext);
    if (!value) {
        throw new Error('useAgent must be used within an AgentProvider');
    }
    return value;
}
